import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { clearSession, readSession, SESSION_CHANGED_EVENT, writeSession } from '../../lib/auth/session'
import { api } from '../../lib/api/endpoints'
import { queryClient } from '../../app/queryClient'

interface AuthContextValue {
  authenticated: boolean
  restored: boolean
  login: (password: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState(() => readSession())
  const [restored, setRestored] = useState(false)

  useEffect(() => {
    const sync = () => setSession(readSession())
    window.addEventListener(SESSION_CHANGED_EVENT, sync)
    setRestored(true)
    return () => window.removeEventListener(SESSION_CHANGED_EVENT, sync)
  }, [])

  const login = useCallback(async (password: string) => {
    const result = await api.login(password)
    writeSession({ accessToken: result.access_token, refreshToken: result.refresh_token })
    setSession(readSession())
  }, [])

  const logout = useCallback(() => {
    clearSession()
    queryClient.clear()
    setSession(null)
  }, [])

  const value = useMemo(() => ({ authenticated: Boolean(session), restored, login, logout }), [session, restored, login, logout])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
